import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/**
 * Partner registration, answered by the new backend.
 *
 * Xano ran this as a six-step wizard against one `channels` row: create it,
 * read the PAN, read the Aadhaar, record the office address, accept
 * commission rates and switch products on, done. The new backend has the
 * same steps under `/organizations/:id/registration/*`, and this file keeps
 * the Xano function signatures the wizard's screens were written against,
 * so the screens did not have to change.
 *
 * Differences worth knowing: the Aadhaar OTP path is gone (Xano had it
 * disabled for months; the desk photographs the card), the PAN is kept as
 * a hash plus a masked form rather than in clear, and the partner's login
 * is not created here.
 */

type Payload<T> = AxiosRequestConfig<T>;

/* ------------------------------------------------------------------ shared */

type OrganizationDetail = {
  id: string;
  name: string;
  type: string;
  partnerLevel: number | null;
  status: string;
  email: string | null;
  phone: string | null;
  assignedUser?: { id: string; fullName: string } | null;
};

type RegistrationStatus = {
  step: 1 | 2 | 3 | 4 | 5 | 6;
  organization: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    status: string;
    partnerLevel: number | null;
    assignedUserId: string | null;
    city: string | null;
    state: string | null;
  };
  registration: {
    category: string | null;
    panMasked: string | null;
    panType: string | null;
    panName: string | null;
    aadhaarLast4: string | null;
    dob: string | null;
    aadhaarAddress: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    state: string | null;
    pincode: string | null;
    gstNumber: string | null;
    propertyType: string | null;
    geolocation: string | null;
    productIds: string[];
    completedAt: string | null;
  } | null;
};

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

/**
 * The partner id reaches this file from localStorage and the query string,
 * so it is checked before it becomes part of a path: an organization id is
 * digits and nothing else. Anything else cannot address a different route.
 */
function organizationPath(id: string | number | undefined, tail = ""): string {
  const value = String(id ?? "").trim();
  if (!/^\d{1,19}$/.test(value)) {
    throw new Error("No partner to work on");
  }
  return `/organizations/${value}${tail}`;
}

function registrationPath(id: string | number | undefined, tail = ""): string {
  return organizationPath(id, `/registration${tail}`);
}

/**
 * The screens hold images as data URLs (the Xano client posted them as
 * strings); the new API wants real multipart files.
 */
function dataUrlToFile(dataUrl: string | undefined, name: string): File | null {
  if (!dataUrl || !dataUrl.startsWith("data:")) return null;
  const [head, body] = dataUrl.split(",", 2);
  const mime = /data:([^;]+)/.exec(head)?.[1] ?? "application/octet-stream";
  const bytes = atob(body);
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
  const ext = mime.split("/")[1]?.replace("jpeg", "jpg") ?? "bin";
  return new File([buf], `${name}.${ext}`, { type: mime });
}

function form(fields: Record<string, string | number | undefined | null>) {
  const fd = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null || value === "") continue;
    fd.append(key, String(value));
  }
  return fd;
}

/* ------------------------------------------------------------------ step 1 */

export type PostPartnerRegistrationStep1RequestType = Partial<{
  /**
   * An existing partner lead to register. Xano's v3 step 1 ran against the
   * lead's own channel id; here the same record is completed rather than a
   * second one created.
   */
  lead_id: string | number;
  /**
   * Only set when completing a lead that already has one. A new partner is
   * created under its email as a placeholder; the PAN step names it, as in
   * Xano.
   */
  name?: string;
  email: string;
  phone: string;
  /** Xano's partner category: ffmc, travel agency, ... or free text. */
  type: string;
  /** The relationship manager's user id. */
  staff_id: string | number;
}>;

export type PostPartnerRegistrationStep1ResponseType = {
  id: string;
  channel: { id: string; name: string; approval_status: string };
  /** False when the partner exists but the manager could not be set. */
  rm_assigned: boolean;
};

/**
 * Creates the partner in `pending`, as Xano did, or completes a lead's
 * basic details. It becomes active only at step 5.
 */
export async function postPartnerRegistrationStep1(
  payload: Payload<PostPartnerRegistrationStep1RequestType>,
): Promise<{ data: PostPartnerRegistrationStep1ResponseType }> {
  const body = payload.data ?? {};
  const name = (body.name ?? "").trim();
  const phone = (body.phone ?? "").trim();
  const email = (body.email ?? "").trim();
  const leadId = body.lead_id ? String(body.lead_id) : "";

  const { data: created } = leadId
    ? await moneefloApi.patch<OrganizationDetail>(organizationPath(leadId), {
        name: name || undefined,
        email: email || undefined,
        phone: phone || undefined,
      })
    : await moneefloApi.post<OrganizationDetail>("/organizations", {
        // An organization must be named, and the name is read off the PAN
        // at step 2. Until then the email stands in, as Xano's nameless
        // pending row did.
        name: name || email || phone,
        type: "partner",
        // Its level is not sent: the API places it one below the caller's
        // organization, and would refuse a level that said otherwise.
        email: email || undefined,
        phone: phone || undefined,
        status: "pending",
      });

  // The record exists from here on. A failure in the follow-ups must not
  // read as "registration failed", or the desk will submit again and hit
  // the duplicate-name check. Both can be redone from the partner's page.
  const staffId = body.staff_id ? String(body.staff_id) : "";
  let rmAssigned = true;
  if (staffId) {
    try {
      await moneefloApi.patch(organizationPath(created.id, "/assignment"), {
        assignedUserId: staffId,
      });
    } catch {
      rmAssigned = false;
    }
  }

  // The organization has no "category" column, so what kind of partner this
  // is (FFMC, travel agency, a freelancer typed under Other) is kept as the
  // first note on the record rather than dropped.
  const category = (body.type ?? "").trim();
  if (category && !leadId) {
    try {
      await moneefloApi.post(organizationPath(created.id, "/interactions"), {
        note: `Registered as ${category}`,
        channel: "other",
      });
    } catch {
      /* a lost note is not a lost partner */
    }
  }

  return {
    data: {
      id: created.id,
      channel: {
        id: created.id,
        name: created.name,
        approval_status: created.status === "active" ? "approved" : "pending",
      },
      rm_assigned: rmAssigned,
    },
  };
}

export type IsDefaultMarginSetRequestType = Partial<{
  headers: { Authorization: string };
  params: { product_id: number };
}>;

/**
 * Xano's gate on opening the wizard. Its precondition compared a count to
 * null, which never fails, so in practice it always opened; kept as a
 * no-op so the screen's call site stays as it was.
 */
export async function isDefaultMarginSet(
  _payload: Payload<IsDefaultMarginSetRequestType>,
): Promise<{ data: { is_default_margin_set: true } }> {
  return { data: { is_default_margin_set: true } };
}

/* ------------------------------------------------------------------ resume */

export type GetPartnerIncompleteStepsRequestType = Partial<{
  channels_id: string | number;
  headers: { Authorization: string };
}>;

export type GetPartnerIncompleteStepsResponseType = {
  /** The step to show, Xano's numbering: 1 details … 5 commission, 6 done. */
  step: number;
  result: {
    id: string;
    name: string;
    email: string;
    phone: string;
    type: string;
    status: string;
    relationshil_manager: string;
    pan_type: string;
    masked_pan_no: string;
    masked_aadhar_no: string;
    address_line_1: string;
    address_line_2: string;
    city: string;
    state: string;
    pincode: string;
    gst_number: string;
    property_type: string;
  };
};

/** Xano's getStep: the record and the first step it still lacks. */
export async function getPartnerIncompleteSteps(
  payload: Payload<GetPartnerIncompleteStepsRequestType> & {
    params?: { channels_id?: string | number };
  },
): Promise<{ data: GetPartnerIncompleteStepsResponseType }> {
  const id = payload.data?.channels_id ?? payload.params?.channels_id;
  if (id === undefined || id === null || id === "") {
    throw new Error("No partner to resume");
  }
  const { data } = await moneefloApi.get<RegistrationStatus>(
    registrationPath(id),
  );
  const org = data.organization;
  const reg = data.registration;
  return {
    data: {
      step: data.step,
      result: {
        id: org.id,
        name: org.name,
        email: org.email ?? "",
        phone: org.phone ?? "",
        type: reg?.category ?? "",
        status: org.status,
        relationshil_manager: org.assignedUserId ?? "",
        pan_type:
          reg?.panType === "individualPan"
            ? "Individual or Person"
            : reg?.panType
              ? "Business"
              : "",
        masked_pan_no: reg?.panMasked ?? "",
        masked_aadhar_no: reg?.aadhaarLast4 ?? "",
        address_line_1: reg?.addressLine1 ?? "",
        address_line_2: reg?.addressLine2 ?? "",
        city: reg?.city ?? org.city ?? "",
        state: reg?.state ?? org.state ?? "",
        pincode: reg?.pincode ?? "",
        gst_number: reg?.gstNumber ?? "",
        property_type: reg?.propertyType ?? "",
      },
    },
  };
}

/* ------------------------------------------------------------------ step 2 */

export type PostPanVerificationRequestType = Partial<{
  channels_id: string | number;
  pan_number: string;
  /** A data URL of the card. */
  pan_front: string;
  pan_type: string;
  headers: { Authorization: string };
}>;

export async function postPanVerification(
  payload: Payload<PostPanVerificationRequestType>,
): Promise<{ data: { masked_pan_no: string; name: string } }> {
  const body = payload.data ?? {};
  const fd = form({
    panNumber: (body.pan_number ?? "").toUpperCase(),
    panType: body.pan_type,
  });
  const file = dataUrlToFile(body.pan_front, "pan");
  if (file) fd.append("front", file);
  const { data } = await moneefloApi.post(
    registrationPath(body.channels_id, "/pan"),
    fd,
    MULTIPART,
  );
  return { data: { masked_pan_no: data.masked_pan_no, name: data.name } };
}

export type PostPanVerifyFallbackRequestType = Partial<{
  channels_id: string | number;
  pan_number: string;
  name: string;
  /** yyyy-mm-dd */
  date: string;
  pan_card: string;
  headers: { Authorization: string };
}>;

/** The desk typed the PAN in because the card could not be read. */
export async function POSTPanVerifyFallback(
  payload: Payload<PostPanVerifyFallbackRequestType>,
): Promise<{ status: number; data: unknown }> {
  const body = payload.data ?? {};
  const fd = form({
    panNumber: (body.pan_number ?? "").toUpperCase(),
    name: body.name,
    dob: body.date,
  });
  const file = dataUrlToFile(body.pan_card, "pan");
  if (file) fd.append("front", file);
  const { data } = await moneefloApi.post(
    registrationPath(body.channels_id, "/pan/manual"),
    fd,
    MULTIPART,
  );
  return { status: 200, data };
}

/* ------------------------------------------------------------------ step 3 */

export type PostAadharVerificationRequestType = Partial<{
  channels_id: string | number;
  aadhaar_front: string;
  aadhaar_back: string;
  headers: { Authorization: string };
}>;

/**
 * The screen reads the result off Xano's Cashfree passthrough shape
 * (`adhaar.front.document_fields.uid` and friends), so that is what comes
 * back, built from what the API extracted.
 */
export async function postAadharVerification(
  payload: Payload<PostAadharVerificationRequestType>,
): Promise<{
  data: {
    adhaar: {
      front: { document_fields: { uid: string; dob: string } };
      back: { document_fields: { address: string } };
    };
  };
}> {
  const body = payload.data ?? {};
  const fd = new FormData();
  const front = dataUrlToFile(body.aadhaar_front, "aadhaar-front");
  const back = dataUrlToFile(body.aadhaar_back, "aadhaar-back");
  if (front) fd.append("front", front);
  if (back) fd.append("back", back);
  const { data } = await moneefloApi.post(
    registrationPath(body.channels_id, "/aadhaar"),
    fd,
    MULTIPART,
  );
  return {
    data: {
      adhaar: {
        front: {
          document_fields: {
            uid: `XXXXXXXX${data.aadhaar_last4 ?? ""}`,
            dob: data.dob ?? "",
          },
        },
        back: { document_fields: { address: data.address ?? "" } },
      },
    },
  };
}

export type PostAadharOTPVerificationRequestType = Partial<{
  request_id: string;
  otp: number;
  channels_id: number | string;
  headers: { Authorization: string };
}>;

/** Xano disabled this path; nothing on the new side sends an OTP. */
export async function postAadharOTPVerification(
  _payload: Payload<PostAadharOTPVerificationRequestType>,
): Promise<never> {
  throw new Error("Aadhaar OTP verification is no longer offered");
}

export type PostAadhaarVerifyFallbackRequestType = Partial<{
  channels_id: string | number;
  aadhar_no: string;
  name: string;
  dob: string;
  address: string;
  aadhar_front_card: string;
  aadhar_back_card: string;
  headers: { Authorization: string };
}>;

export async function POSTAadhaarVerifyFallback(
  payload: Payload<PostAadhaarVerifyFallbackRequestType>,
): Promise<{ status: number; data: unknown }> {
  const body = payload.data ?? {};
  const fd = form({
    aadhaarNumber: (body.aadhar_no ?? "").replace(/\D/g, ""),
    name: body.name,
    dob: body.dob,
    address: body.address,
  });
  const front = dataUrlToFile(body.aadhar_front_card, "aadhaar-front");
  const back = dataUrlToFile(body.aadhar_back_card, "aadhaar-back");
  if (front) fd.append("front", front);
  if (back) fd.append("back", back);
  const { data } = await moneefloApi.post(
    registrationPath(body.channels_id, "/aadhaar/manual"),
    fd,
    MULTIPART,
  );
  return { status: 200, data };
}

/* ------------------------------------------------------------------ step 4 */

export type PostOfficeAddressVerificationStep4RequestType = Partial<{
  channels_id: number | string;
  address_line_1: string;
  address_line_2: string;
  city: string | number;
  state: string;
  pincode: number | string;
  photo_1: string;
  photo_2: string;
  photo_3: string;
  photo_4: string;
  gst_number: string;
  property_type: string;
  address_proof: string;
  geolocation: string;
  headers: { Authorization: string };
}>;

export async function postOfficeAddressStep4Verification(
  payload: Payload<PostOfficeAddressVerificationStep4RequestType>,
): Promise<{ data: unknown }> {
  const body = payload.data ?? {};
  const fd = form({
    addressLine1: body.address_line_1,
    addressLine2: body.address_line_2,
    city: body.city,
    state: body.state,
    pincode: body.pincode,
    gstNumber: body.gst_number,
    propertyType: body.property_type,
    geolocation: body.geolocation,
  });
  const proof = dataUrlToFile(body.address_proof, "address-proof");
  if (proof) fd.append("addressProof", proof);
  [body.photo_1, body.photo_2, body.photo_3, body.photo_4].forEach((p, i) => {
    const photo = dataUrlToFile(p, `office-${i + 1}`);
    if (photo) fd.append("photos", photo);
  });
  const { data } = await moneefloApi.post(
    registrationPath(body.channels_id, "/address"),
    fd,
    MULTIPART,
  );
  return { data };
}

export type GetStateRequestType = Partial<{ headers: { Authorization: string } }>;

export async function getState(
  _payload: Payload<GetStateRequestType>,
): Promise<{ data: { id: number | string; name: string }[] }> {
  const { data } = await moneefloApi.get("/catalog/states");
  return {
    data: (data ?? []).map((s: { id: string; name: string }) => ({
      id: s.id,
      name: s.name,
    })),
  };
}

export type GetCityRequestType = Partial<{
  state_id: string | number;
  headers: { Authorization: string };
}>;

export async function getCity(
  payload: Payload<GetCityRequestType> & {
    params?: { state_id?: string | number };
  },
): Promise<{ data: { id: number | string; name: string }[] }> {
  const stateId = payload.params?.state_id ?? payload.data?.state_id;
  const { data } = await moneefloApi.get("/catalog/cities", {
    params: { stateId: stateId ? String(stateId) : undefined },
  });
  return {
    data: (data ?? []).map((c: { id: string; name: string }) => ({
      id: c.id,
      name: c.name,
    })),
  };
}

/* ------------------------------------------------------------------ step 5 */

type DefaultRate = {
  id: string;
  purposeId: string | null;
  purposeName: string;
  productId: string | null;
  productName: string | null;
  ratePerUnit: string;
};

export type GetCommisionRatesStep5RequestType = Partial<{
  headers: { Authorization: string };
  /** The product toggle asks for one product's purposes at a time. */
  params: { product_id?: string | number };
}>;

/**
 * The parent's default commission rates, in the row shape Xano's
 * `default_commision_rates` table had (`purpose_id`, `charges`, and the
 * purpose joined in as `_purpose_mapper`), which is what the product and
 * rate components render.
 */
export async function getCommisionRatesStep5(
  payload: Payload<GetCommisionRatesStep5RequestType> & {
    params?: { product_id?: string | number };
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<{ data: any[] }> {
  // The defaults are the partner's parent's, so the partner being
  // registered is named, as it is for every other step.
  const { data } = await moneefloApi.get<DefaultRate[]>(
    registrationPath(localStorage.getItem("child_channel_id") ?? "", "/defaults"),
  );
  const productId = payload?.params?.product_id;
  return {
    data: (data ?? [])
      .filter((r) => r.purposeId !== null)
      .filter((r) => !productId || String(r.productId) === String(productId))
      .map((r) => ({
        id: r.id,
        purpose_id: r.purposeId,
        product_id: r.productId,
        charges: Number(r.ratePerUnit),
        _purpose_mapper: {
          id: r.purposeId,
          name: r.purposeName,
          product_id: r.productId,
        },
      })),
  };
}

export type GetAllProductsRequestType = Partial<{
  headers: { Authorization: string };
}>;

export async function getAllProducts(
  _payload: Payload<GetAllProductsRequestType>,
): Promise<{ data: { id: string; name: string; status: string }[] }> {
  const { data } = await moneefloApi.get("/catalog/products");
  return {
    data: (data ?? []).map(
      (p: { id: string; name: string; isActive: boolean }) => ({
        id: p.id,
        name: p.name,
        status: p.isActive ? "active" : "inactive",
      }),
    ),
  };
}

type RateRow = { purpose_id: string | number; charges: number };

export type PostUpdateCommisionRatesStep5RequestType = Partial<{
  child_channel: string | number;
  accept_default: boolean;
  products: (string | number)[];
  commision_rates: RateRow[];
  unapproved_commision_rates: RateRow[];
  approved_commision_rates: RateRow[];
  headers: { Authorization: string };
}>;

/**
 * Step 5. The screen has three ways of sending rates (accept defaults, a
 * full edited list, or an approved/unapproved split); they all mean "these
 * are the rates", so they are folded into one list here.
 */
export async function postUpdateCommisionRatesStep5(
  payload: Payload<PostUpdateCommisionRatesStep5RequestType>,
): Promise<{ data: { channel: { id: string; approval_status: string } } }> {
  const body = payload.data ?? {};
  const edited = [
    ...(body.commision_rates ?? []),
    ...(body.unapproved_commision_rates ?? []),
    ...(body.approved_commision_rates ?? []),
  ].filter((r) => r && r.purpose_id !== undefined && r.purpose_id !== null);

  let products = (body.products ?? []).map(String);
  if (products.length === 0) {
    // The edit path does not carry the product list; the wizard mirrors
    // the switched-on products into localStorage for exactly this reason.
    try {
      products = JSON.parse(localStorage.getItem("enabled_products") ?? "[]");
    } catch {
      products = [];
    }
  }

  const { data } = await moneefloApi.post<RegistrationStatus>(
    registrationPath(body.child_channel, "/complete"),
    {
      products,
      acceptDefault: body.accept_default !== false || edited.length === 0,
      commissionRates: edited.length
        ? edited.map((r) => ({
            purposeId: String(r.purpose_id),
            ratePerUnit: Number(r.charges),
          }))
        : undefined,
    },
  );
  return {
    data: {
      channel: {
        id: data.organization.id,
        approval_status:
          data.organization.status === "active" ? "approved" : "pending",
      },
    },
  };
}
